import { z } from 'zod';
import { ALERT_TYPES, WEBHOOK_FORMATS } from '../constants.js';
/**
 * Réglages d'une organisation : dépôt de sauvegarde, canaux d'alerte, fenêtre de mise à jour.
 *
 * Ces réglages vivent en base et non en variables d'environnement, pour être modifiables à chaud
 * sans redéployer — un dépôt S3 qu'on change, un webhook qu'on ajoute, une fenêtre qu'on décale ne
 * justifient pas un redémarrage du plan de contrôle.
 *
 * Les secrets (clé secrète S3, secret HMAC) ne sont jamais renvoyés par l'API : ils s'écrivent, se
 * remplacent, mais ne se relisent pas. C'est la même règle que pour les secrets de configuration
 * d'un site, à une différence près : ici, personne n'a besoin de les relire depuis Vigie.
 */
/**
 * Portées modifiables depuis une organisation.
 *
 * `global` en est délibérément absente : elle s'applique à toutes les organisations de l'instance,
 * et l'exposer ici permettrait à l'administration d'une organisation d'agir sur les autres, ce que
 * le scoping par organisation interdit. Un dépôt global se pose à l'installation.
 */
export const EDITABLE_STORAGE_SCOPES = ['organization', 'site'];
export const storageConfigInputSchema = z
    .object({
    scope: z.enum(EDITABLE_STORAGE_SCOPES),
    /** Requis pour la portée `site`, interdit sinon. */
    siteId: z.string().uuid().optional(),
    endpoint: z
        .string()
        .url('Une URL complète est attendue, schéma compris')
        .refine((valeur) => /^https?:\/\//.test(valeur), {
        message: 'Le schéma doit être http ou https',
    }),
    bucket: z.string().min(1).max(255),
    region: z.string().min(1).max(64).default('eu-west-1'),
    prefix: z.string().max(255).default(''),
    accessKey: z.string().min(1),
    secretKey: z.string().min(1),
})
    .refine((valeur) => (valeur.scope === 'site') === (valeur.siteId !== undefined), {
    message: 'Un dépôt de portée site exige un siteId, et lui seul.',
    path: ['siteId'],
});
export const webhookInputSchema = z.object({
    name: z.string().min(1).max(120),
    url: z
        .string()
        .url()
        .refine((valeur) => /^https?:\/\//.test(valeur), {
        message: 'Le schéma doit être http ou https',
    }),
    /** Facultatif : un canal dédié à un seul site de l'organisation. */
    siteId: z.string().uuid().nullish(),
    /** Null ou absent = tous les types d'alerte. */
    events: z.array(z.enum(ALERT_TYPES)).min(1).nullish(),
    /**
     * Secret de signature. Absent = inchangé à la mise à jour ; chaîne vide = signature retirée.
     * Sans distinction entre les deux, on ne pourrait jamais désactiver une signature déjà posée.
     */
    hmacSecret: z.string().max(255).optional(),
    /**
     * Forme de la charge envoyée. `slack` est indispensable pour un webhook entrant Slack, qui refuse
     * tout JSON qui n'est pas le sien — un canal mal typé échouerait à chaque alerte.
     */
    format: z.enum(WEBHOOK_FORMATS).default('vigie'),
    enabled: z.boolean().default(true),
});
export const updateWindowInputSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    startHour: z.number().int().min(0).max(23),
    endHour: z.number().int().min(0).max(23),
    /** Nom de zone IANA, vérifié à l'exécution : la liste évolue avec l'environnement. */
    timeZone: z.string().min(1).max(64),
})
    .refine((valeur) => valeur.startHour !== valeur.endHour, {
    message: 'Une fenêtre de début et de fin identiques serait vide.',
    path: ['endHour'],
});
/** La zone est-elle connue de l'environnement ? */
export function isKnownTimeZone(timeZone) {
    try {
        new Intl.DateTimeFormat('fr-FR', { timeZone });
        return true;
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=settings.js.map